import { useState, useEffect } from 'react';
import { canInstall, showInstallPrompt, isPWA } from '../utils/pwa';

interface InstallPromptProps {
  variant?: 'banner' | 'modal' | 'button';
}

export function InstallPrompt({ variant = 'banner' }: InstallPromptProps) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already installed as PWA
    if (isPWA()) return;

    // Check if user previously dismissed
    const previouslyDismissed = localStorage.getItem('nt3d_install_dismissed');
    if (previouslyDismissed) {
      const dismissedTime = parseInt(previouslyDismissed);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Wait for install prompt to be available
    const checkInstall = () => {
      if (canInstall()) {
        setShowPrompt(true);
      }
    };

    // Check immediately and after a delay
    checkInstall();
    const timeout = setTimeout(checkInstall, 2000);

    // Listen for custom event
    window.addEventListener('beforeinstallprompt', () => setShowPrompt(true));

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const handleInstall = async () => {
    const installed = await showInstallPrompt();
    if (installed) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowPrompt(false);
    localStorage.setItem('nt3d_install_dismissed', Date.now().toString());
  };

  if (!showPrompt || dismissed) return null;

  if (variant === 'button') {
    return (
      <button
        onClick={handleInstall}
        className="
          flex items-center gap-2 px-4 py-2 rounded-lg
          bg-gradient-to-r from-cyan-500 to-purple-500
          text-white font-semibold
          hover:from-cyan-400 hover:to-purple-400
          transition-all duration-200
          shadow-lg shadow-cyan-500/25
        "
      >
        <span>📱</span>
        <span>Install App</span>
      </button>
    );
  }

  if (variant === 'modal') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
        <div className="bg-gray-900 rounded-2xl max-w-md w-full p-6 border border-cyan-500/30">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">📱</div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Install No Tomorrow 3D
            </h2>
            <p className="text-gray-400">
              Get instant access, push notifications, and a faster experience!
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3 text-gray-300">
              <span className="text-green-400">✓</span>
              <span>Works offline</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300">
              <span className="text-green-400">✓</span>
              <span>Real-time event notifications</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300">
              <span className="text-green-400">✓</span>
              <span>Faster loading times</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300">
              <span className="text-green-400">✓</span>
              <span>Home screen shortcut</span>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={handleDismiss}
              className="flex-1 py-3 rounded-xl border border-gray-700 text-gray-400 hover:bg-white/5 transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={handleInstall}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold hover:from-cyan-400 hover:to-purple-400 transition-all"
            >
              Install Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Default banner variant
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 p-4 bg-gradient-to-r from-gray-900 to-gray-800 border-t border-cyan-500/30">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="text-3xl">📱</div>
          <div>
            <div className="font-semibold text-white">
              Install No Tomorrow 3D
            </div>
            <div className="text-sm text-gray-400">
              Get push notifications & faster loading!
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            ✕
          </button>
          <button
            onClick={handleInstall}
            className="px-6 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold hover:from-cyan-400 hover:to-purple-400 transition-all"
          >
            Install
          </button>
        </div>
      </div>
    </div>
  );
}

export default InstallPrompt;
